/** The reframe timestamps and the video decoder both use source-video time. */
export function reframeSourceTimeMs({
  videoStartFromInSeconds,
  assetStartDeltaInSeconds,
  frame,
  fps,
  playbackRate,
}: {
  videoStartFromInSeconds: number;
  assetStartDeltaInSeconds: number;
  frame: number;
  fps: number;
  playbackRate: number;
}): number {
  if (![videoStartFromInSeconds, assetStartDeltaInSeconds, frame, fps, playbackRate].every(Number.isFinite) ||
      fps <= 0 || playbackRate <= 0) {
    throw Error('리프레임 영상의 재생 시간을 확인하지 못했습니다.');
  }
  return Math.max(0, videoStartFromInSeconds + assetStartDeltaInSeconds +
    Math.max(0, frame) * playbackRate / fps) * 1000;
}

type Bbox = readonly [number, number, number, number];
type ReframeKeyframe = {timestampMs: number; bbox: Bbox};
type ReframeSegment = {mode: 'single' | 'dual'; startMs: number; endMs: number; tracks: number[]};
type ReframeMetadata = {
  segments: ReframeSegment[];
  bboxKeyframes: Record<string, ReframeKeyframe[]>;
};

const MAX_REFRAME_SEGMENTS = 10_000;
const MAX_REFRAME_TRACKS = 128;
const MAX_REFRAME_KEYFRAMES_PER_TRACK = 100_000;
const MAX_REFRAME_TOTAL_KEYFRAMES = 250_000;
const MAX_REFRAME_DISPLAY_DIMENSION = 8192;
const MAX_REFRAME_DISPLAY_PIXELS = 16 * 1024 * 1024;

/** Validate the saved tracker data before browser export can decode or draw it. */
export function assertAlphaReframeMetadata(meta: unknown, width: number, height: number):
  asserts meta is ReframeMetadata {
  const invalid = () => { throw Error('투명 영상 리프레임 좌표가 올바르지 않습니다. 원본 분석을 다시 실행해 주세요.'); };
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 ||
      !meta || typeof meta !== 'object') invalid();
  const data = meta as Partial<ReframeMetadata>;
  if (!Array.isArray(data.segments) || data.segments.length === 0 ||
      data.segments.length > MAX_REFRAME_SEGMENTS ||
      !data.bboxKeyframes || typeof data.bboxKeyframes !== 'object') invalid();
  const validatedTracks = new Set<number>();
  let totalKeyframes = 0;
  for (const segment of data.segments!) {
    if (!segment || !['single', 'dual'].includes(segment.mode) ||
        !Number.isFinite(segment.startMs) || !Number.isFinite(segment.endMs) ||
        segment.startMs < 0 || segment.endMs <= segment.startMs ||
        !Array.isArray(segment.tracks) ||
        segment.tracks.length !== (segment.mode === 'dual' ? 2 : 1) ||
        new Set(segment.tracks).size !== segment.tracks.length) invalid();
    for (const track of segment.tracks) {
      if (!Number.isSafeInteger(track)) invalid();
      if (validatedTracks.has(track)) continue;
      validatedTracks.add(track);
      if (validatedTracks.size > MAX_REFRAME_TRACKS) invalid();
      const keyframes = data.bboxKeyframes![String(track)];
      if (!Array.isArray(keyframes) || keyframes.length === 0 ||
          keyframes.length > MAX_REFRAME_KEYFRAMES_PER_TRACK) invalid();
      totalKeyframes += keyframes.length;
      if (totalKeyframes > MAX_REFRAME_TOTAL_KEYFRAMES) invalid();
      let previous = -Infinity;
      for (const keyframe of keyframes) {
        const bbox = keyframe?.bbox;
        if (!Number.isFinite(keyframe?.timestampMs) || keyframe.timestampMs < previous ||
            !Array.isArray(bbox) || bbox.length !== 4 || !bbox.every(Number.isFinite) ||
            bbox[2] - bbox[0] < 10 || bbox[3] - bbox[1] < 10 ||
            bbox[0] < -width || bbox[1] < -height ||
            bbox[2] > width * 2 || bbox[3] > height * 2 ||
            bbox[2] <= 0 || bbox[0] >= width || bbox[3] <= 0 || bbox[1] >= height) invalid();
        previous = keyframe.timestampMs;
      }
    }
  }
}

/** A dual crop mounts two independent source-sized video decoders. */
export function alphaReframeDecoderCount(meta: ReframeMetadata): 1 | 2 {
  return meta.segments.some((segment) => segment.mode === 'dual') ? 2 : 1;
}

/** Only the first crop owns the source audio, even when two videos decode. */
export function reframeCropMuted(baseMuted: boolean, cropIndex: 0 | 1): boolean {
  return baseMuted || cropIndex === 1;
}

export interface ReframeCropRect {left: number; top: number; width: number; height: number}

export function reframeVideoStyle(crop: ReframeCropRect, containerWidth: number,
  containerHeight: number, sourceVideoWidth: number, sourceVideoHeight: number,
  browserAlpha = false) {
  if (![crop.left, crop.top, crop.width, crop.height, containerWidth, containerHeight,
    sourceVideoWidth, sourceVideoHeight].every(Number.isFinite) ||
    crop.left < 0 || crop.top < 0 || crop.width <= 0 || crop.height <= 0 ||
    crop.left + crop.width > 1.000001 || crop.top + crop.height > 1.000001 ||
    containerWidth <= 0 || containerHeight <= 0 || sourceVideoWidth <= 0 || sourceVideoHeight <= 0)
    throw Error('투명 영상 리프레임 출력 좌표가 올바르지 않습니다.');
  const cropPxW = crop.width * sourceVideoWidth;
  const cropPxH = crop.height * sourceVideoHeight;
  const scale = Math.max(containerWidth / cropPxW, containerHeight / cropPxH);
  if (!Number.isFinite(scale) || !Number.isFinite(sourceVideoWidth * scale) ||
      !Number.isFinite(sourceVideoHeight * scale))
    throw Error('투명 영상 리프레임 출력 좌표가 올바르지 않습니다.');
  const displayWidth = sourceVideoWidth * scale;
  const displayHeight = sourceVideoHeight * scale;
  // Match the existing logical RGBA group surface budget. The native decoder
  // separately checks the physical source frame and declared pixel count.
  if (browserAlpha && (displayWidth > MAX_REFRAME_DISPLAY_DIMENSION ||
      displayHeight > MAX_REFRAME_DISPLAY_DIMENSION ||
      Math.ceil(displayWidth) * Math.ceil(displayHeight) > MAX_REFRAME_DISPLAY_PIXELS))
    throw Error('투명 영상 리프레임 확대 영역이 브라우저 출력 한도를 넘었습니다. 크롭 범위를 넓혀 다시 시도해 주세요.');
  return {
    position: 'absolute' as const,
    width: displayWidth,
    height: displayHeight,
    left: -crop.left * sourceVideoWidth * scale + (containerWidth - cropPxW * scale) / 2,
    top: -crop.top * sourceVideoHeight * scale + (containerHeight - cropPxH * scale) / 2,
    maxWidth: 'unset' as const,
    filter: 'brightness(1)',
    WebkitBackfaceVisibility: 'hidden' as const,
    backfaceVisibility: 'hidden' as const,
  };
}
